<template>
  <div class="min-h-screen py-8">
    <Head title="Conciliación Bancaria Excel" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">Conciliación / Relleno de Pagos con Excel</h1>
          <p class="text-sm text-gray-600 mt-1">
            Carga el extracto bancario en formato Excel para cruzar las referencias registradas y completar automáticamente el monto en Bs. y la tasa de cambio utilizada.
          </p>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div class="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex items-center justify-between">
          <div>
            <p class="text-xs font-semibold text-amber-600 uppercase tracking-wider">Pagos Pendientes por Datos Bs</p>
            <p class="text-3xl font-extrabold text-gray-900 mt-1">{{ summary.totalPending }}</p>
          </div>
          <div class="p-3 bg-amber-100 rounded-full text-amber-600">
            <span class="material-icons-outlined text-2xl">pending_actions</span>
          </div>
        </div>

        <div class="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex items-center justify-between">
          <div>
            <p class="text-xs font-semibold text-green-600 uppercase tracking-wider">Pagos con Datos en Bs Completados</p>
            <p class="text-3xl font-extrabold text-gray-900 mt-1">{{ summary.totalWithAmountBs }}</p>
          </div>
          <div class="p-3 bg-green-100 rounded-full text-green-600">
            <span class="material-icons-outlined text-2xl">check_circle</span>
          </div>
        </div>
      </div>

      <!-- Upload Form Card -->
      <div class="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-8">
        <h2 class="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <span class="material-icons-outlined text-indigo-600 mr-2">upload_file</span>
          Cargar Reporte de Movimientos Bancarios (Excel)
        </h2>

        <form @submit.prevent="submitFile" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Seleccione o arrastre el archivo Excel (.xlsx, .xls, .csv)</label>
            <input 
              type="file" 
              ref="fileInput" 
              accept=".xlsx,.xls,.csv"
              @change="handleFileChange"
              class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 border border-gray-300 rounded-md p-2"
            />
            <p v-if="form.errors.excel_file" class="mt-1 text-sm text-red-600">
              {{ form.errors.excel_file }}
            </p>
          </div>

          <div class="flex justify-end">
            <button 
              type="submit" 
              :disabled="form.processing || !selectedFile"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 disabled:opacity-50"
            >
              <svg v-if="form.processing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="material-icons-outlined text-sm mr-2" v-else>sync</span>
              Procesar y Conciliar Pagos
            </button>
          </div>
        </form>
      </div>

      <!-- Results Display Section -->
      <div v-if="results" class="space-y-6 mb-8">
        <!-- Success Banner -->
        <div class="bg-green-50 border-l-4 border-green-400 p-4 rounded-md flex items-center justify-between">
          <div class="flex items-center">
            <span class="material-icons-outlined text-green-500 mr-3 text-2xl">check_circle</span>
            <div>
              <p class="text-sm font-medium text-green-800">Procesamiento Exitoso</p>
              <p class="text-xs text-green-700 mt-0.5">
                Se actualizaron <strong>{{ results.updatedCount }}</strong> pagos de un total de <strong>{{ results.totalProcessed }}</strong> movimientos bancarios analizados.
              </p>
            </div>
          </div>
        </div>

        <!-- Updated Payments Table -->
        <div v-if="results.updatedPayments && results.updatedPayments.length > 0" class="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
          <div class="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h3 class="text-base font-semibold text-gray-900 flex items-center">
              <span class="material-icons-outlined text-green-600 mr-2">task_alt</span>
              Pagos Actualizados en Base de Datos ({{ results.updatedPayments.length }})
            </h3>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID Pago</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cuota / Cliente</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Método</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referencia</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto USD</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto Bs (Excel)</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tasa Calculada</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="pay in results.updatedPayments" :key="pay.id" class="hover:bg-gray-50">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-semibold text-indigo-600">
                    #{{ pay.id }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <Link :href="`/orders/${pay.order_id}`" class="hover:underline font-medium text-indigo-600">
                      Cuota #{{ pay.order_id }}
                    </Link>
                    <span class="text-xs text-gray-500 block">{{ pay.client_name }}</span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                    {{ pay.method }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                    {{ pay.reference }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    ${{ Number(pay.amount_usd).toFixed(2) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-green-700 font-medium">
                    Bs. {{ Number(pay.amount_bs).toFixed(2) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-indigo-700 font-bold">
                    {{ Number(pay.change).toFixed(4) }} Bs/$
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Unmatched Bank Rows Table -->
        <div v-if="results.unmatchedBankRows && results.unmatchedBankRows.length > 0" class="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
          <div class="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h3 class="text-base font-semibold text-gray-900 flex items-center">
              <span class="material-icons-outlined text-amber-600 mr-2">help_outline</span>
              Movimientos del Excel No Asociados a Pagos Registrados ({{ results.unmatchedBankRows.length }})
            </h3>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referencia Banco</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto Bs</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="(bankRow, k) in results.unmatchedBankRows" :key="k" class="hover:bg-gray-50">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {{ bankRow.date }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {{ bankRow.reference }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    Bs. {{ Number(bankRow.amount_bs).toFixed(2) }}
                  </td>
                  <td class="px-6 py-4 text-xs text-gray-500 truncate max-w-md">
                    {{ bankRow.description }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Pending OrderPays in DB Table -->
      <div class="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <div class="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h3 class="text-base font-semibold text-gray-900 flex items-center">
            <span class="material-icons-outlined text-gray-600 mr-2">list_alt</span>
            Pagos Registrados en Sistema Pendientes por Datos en Bs ({{ pendingPays.length }})
          </h3>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID Pago</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cuota / Cliente</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Método</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto ($ USD)</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referencia BD</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Registro</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="pay in pendingPays" :key="pay.id" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  #{{ pay.id }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <Link :href="`/orders/${pay.order_id}`" class="hover:underline font-medium text-indigo-600">
                    Cuota #{{ pay.order_id }}
                  </Link>
                  <span class="text-xs text-gray-500 block">{{ pay.order ? (pay.order.client ? pay.order.client.name : 'Sin cliente') : 'Sin orden' }}</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                  {{ pay.method }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                  ${{ Number(pay.amount).toFixed(2) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                  {{ pay.reference || '-' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ pay.created_at }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <Link 
                    :href="`/orders/${pay.order_id}`" 
                    class="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Ver Cuota
                  </Link>
                </td>
              </tr>
              <tr v-if="pendingPays.length === 0">
                <td colspan="7" class="px-6 py-8 text-center text-sm text-gray-500">
                  ¡Excelente! No hay pagos pendientes por completar datos en Bs.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
  components: {
    Head,
    Link,
  },
  layout: Layout,
  props: {
    pendingPays: {
      type: Array,
      default: () => []
    },
    summary: {
      type: Object,
      default: () => ({ totalPending: 0, totalWithAmountBs: 0 })
    },
    results: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      selectedFile: null,
      form: this.$inertia.form({
        excel_file: null,
      })
    }
  },
  methods: {
    handleFileChange(event) {
      const file = event.target.files[0]
      this.selectedFile = file
      this.form.excel_file = file
    },
    submitFile() {
      if (!this.selectedFile) return;
      this.form.post('/payments/reconcile', {
        preserveScroll: true,
      })
    }
  }
}
</script>
